import type { PaywallEvent } from '@sharefile/ecomm-models';
import { EntitlementsContentParams } from '@sharefiledev/entitlements-pilet-sdk';
import {
	EvaluateRequest,
	EvaluateRequestContent,
	EvaluationResponse,
	NotificationRequest,
	PaywallBannerContentResponse,
	PaywallModalContentResponse,
} from '../components/types';

interface Configuration {
	origin: string;
	apiPath: string;
}

const configuration: Configuration = {
	origin: window.origin,
	apiPath: '/io/entitlements/api/v1/',
};

export function configureHttp(configure: (current: Configuration) => void) {
	configure(configuration);
}

export function createRequest(
	path: string,
	locale: string | null,
	init: RequestInit
): Request {
	const url: URL = new URL(`${configuration.origin}${configuration.apiPath}${path}`);
	if (locale) {
		url.searchParams.append('locale', locale);
	}
	init.headers = { ...init.headers, 'X-BFF-CSRF': 'true' };
	return new Request(url, init);
}

export interface ContentAPIRequestParams extends EntitlementsContentParams {
	isAdmin: boolean;
	isFreeTrial: boolean;
	userRID: string;
}

function getAPIRequestContainer(
	params: EntitlementsContentParams,
	isFreeTrial: boolean,
	isAdmin: boolean,
	userRID: string
): ContentAPIRequestParams {
	const requestParams: ContentAPIRequestParams = {
		entitlementDefRID: params.entitlementDefRID,
		entitlementRID: params.entitlementRID,
		entitled: params.entitled,
		evaluationResult: {
			isPaywallEnabled: params.evaluationResult.isPaywallEnabled,
			showLimitBanner: params.evaluationResult.showLimitBanner,
			limit: params.evaluationResult.limit,
			current: params.evaluationResult.current,
			requested: params.evaluationResult.requested,
			remaining: params.evaluationResult.remaining,
		},
		httpStatusCodeResponseToCaller: Number(params.httpStatusCodeResponseToCaller),
		isAdmin,
		isFreeTrial,
		userRID,
	};
	if (!!params.storageInfo) {
		requestParams.storageInfo = params.storageInfo;
	}
	return requestParams;
}

export const EntitlementsHttp = {
	async retrieveModalAssets(
		params: EntitlementsContentParams,
		isFreeTrial: boolean,
		isAdmin: boolean,
		locale: string,
		userRID: string
	): Promise<PaywallModalContentResponse> {
		const requestParams = getAPIRequestContainer(params, isFreeTrial, isAdmin, userRID);
		const request = createRequest('entitlements/assets/modal', locale, {
			method: 'POST',
			body: JSON.stringify(requestParams),
			headers: {
				'content-type': 'application/json',
			},
		});
		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get modal assets',
		});
		const assets: PaywallModalContentResponse = await response.json();
		return assets;
	},

	async retrieveBannerAssets(
		params: EntitlementsContentParams,
		isFreeTrial: boolean,
		isAdmin: boolean,
		locale: string,
		userRID: string
	): Promise<PaywallBannerContentResponse> {
		const requestParams = getAPIRequestContainer(params, isFreeTrial, isAdmin, userRID);

		const request = createRequest('entitlements/assets/banner', locale, {
			method: 'POST',
			body: JSON.stringify(requestParams),
			headers: {
				'content-type': 'application/json',
			},
		});

		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get banner assets',
		});

		const assets: PaywallBannerContentResponse = await response.json();
		return assets;
	},

	async sendNotificationRequest(
		notificationRequest: NotificationRequest,
		locale: string
	) {
		const request = createRequest('paywall/notification', locale, {
			method: 'POST',
			body: JSON.stringify(notificationRequest),
			headers: {
				'content-type': 'application/json',
			},
		});
		await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to send notification email',
		});
	},

	async sendEvaluateRequest(
		evaluateRequest: EvaluateRequest,
		userRID: string
	): Promise<EntitlementsContentParams | undefined> {
		const evaluationRequestContent: EvaluateRequestContent = {
			userRID,
			evaluationRequests: [evaluateRequest],
		};
		const request = createRequest('entitlementdefs/evaluate', null, {
			method: 'POST',
			body: JSON.stringify(evaluationRequestContent),
			headers: {
				'content-type': 'application/json',
			},
		});
		try {
			const response = await fetchOrThrowErrorFromResponse(request, {
				expectedStatus: 200,
				defaultErrorMessage: 'Unable to evaluate',
			});
			const evaluateResponse: EvaluationResponse = await response.json();
			if (evaluateResponse.results.length > 0) {
				return evaluateResponse.results[0];
			}
		} catch {}
		return;
	},

	async recordPaywallEvent(event: PaywallEvent): Promise<void> {
		const request = createRequest('paywall/events', null, {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(event),
		});
		try {
			await fetchOrThrowErrorFromResponse(request, {
				expectedStatus: 200,
				defaultErrorMessage: 'Unable to record paywall event',
			});
		} catch {}
	},
};

async function fetchOrThrowErrorFromResponse(
	request: Request,
	config: { expectedStatus?: number; defaultErrorMessage?: string } = {}
) {
	const response = await fetch(request);
	const isError =
		config.expectedStatus !== undefined
			? response.status !== config.expectedStatus
			: response.status > 399;

	if (isError) {
		let errorBody: any = {};
		if (response.headers.get('content-type')?.includes('application/json')) {
			errorBody = await response.json().catch(_ => ({}));
		}
		const message =
			errorBody?.message ??
			errorBody?.Message ??
			config.defaultErrorMessage ??
			'Unknown error';
		throw new EntitlementsNetworkError(
			message,
			response,
			errorBody?.statusCode,
			errorBody
		);
	}
	return response;
}

export class EntitlementsNetworkError extends Error {
	public constructor(
		public message: string,
		public response: Response,
		public status?: number,
		public body?: any
	) {
		super(message);
		this.status = status ?? response.status;
	}
}
